/**
 * Constants for Sidewall & Burner Tracking
 * Must match seed.sql values in database
 */

// ============================================
// Status Codes (shared with Roof)
// ============================================

export { STATUS_CODE, isValidStatusCode } from "../../../../shared/models";
export type { StatusCode } from "../../../../shared/models";

// ============================================
// Part Type IDs (from PartType table)
// ============================================

export const PART_TYPE_IDS = {
  // Container types
  EAF_SIDEWALL: 200,
  EAF_SPARES: 201,
  // Spares container types (202-212)
  PANEL_SPARES: 202,
  ARCHWAY_SPARES: 203,
  SLAG_DOOR_SPARES: 204,
  SUMP_SPARES: 205,
  SHELL_SPARES: 206,
  BURNER_BLOCK_SPARES: 207,
  BURNER_FRONT_FACE_SPARES: 208,
  BURNER_WATER_JACKET_SPARES: 209,
  BURNER_NOZZLE_SPARES: 210,
  SIDEWALL_SPARES: 211,
  SIDEWALL_PARTS_SPARES: 212,

  // Slot types (permanent containers, like Caster Strands — NOT replaceable)
  UPPER_SHELL_SLOT: 312, // Permanent position for Upper Shell parts
  LOWER_SHELL_SLOT: 313, // Permanent position for Lower Shell parts

  // Replaceable parts (300+)
  SIDEWALL_PANEL: 301,
  SIDEWALL_BURNER_PANEL: 302,
  ARCHWAY: 303,
  SLAG_DOOR: 304,
  SUMP: 305,
  UPPER_SHELL: 306, // Upper Shell type — replaceable, installed on Upper Shell Slot
  BURNER_BLOCK: 307,
  BURNER_FRONT_FACE: 308,
  BURNER_WATER_JACKET: 309,
  BURNER_NOZZLE: 310,
  LOWER_SHELL: 311, // Lower Shell type — replaceable, installed on Lower Shell Slot
} as const satisfies Record<string, number>;

/** Shell type IDs (both Upper and Lower — replaceable parts) */
export const SHELL_TYPE_IDS = [
  PART_TYPE_IDS.UPPER_SHELL,
  PART_TYPE_IDS.LOWER_SHELL,
] as const;

/** Shell slot type IDs (permanent containers — like Strands) */
export const SHELL_SLOT_TYPE_IDS = [
  PART_TYPE_IDS.UPPER_SHELL_SLOT,
  PART_TYPE_IDS.LOWER_SHELL_SLOT,
] as const;

/** Check if a PartTypeId is a shell type (Upper or Lower — replaceable) */
export const isShellType = (partTypeId: number | null | undefined): boolean => {
  if (partTypeId == null) return false;
  return (SHELL_TYPE_IDS as readonly number[]).includes(partTypeId);
};

/** Check if a PartTypeId is a shell slot type (permanent container) */
export const isShellSlotType = (
  partTypeId: number | null | undefined,
): boolean => {
  if (partTypeId == null) return false;
  return (SHELL_SLOT_TYPE_IDS as readonly number[]).includes(partTypeId);
};

/** Type for part type ID values */
export type PartTypeId = (typeof PART_TYPE_IDS)[keyof typeof PART_TYPE_IDS];

// ============================================
// Part IDs (from Part table)
// ============================================

export const PART_IDS = {
  // Container IDs
  SIDEWALL: 500,
  SPARES: 501,
  SHELL_SPARES: 502,
  SHELL_PARTS_SPARES: 503,

  // Spares container IDs
  PANEL_SPARES: 510,
  ARCHWAY_SPARES: 511,
  SLAG_DOOR_SPARES: 512,
  SUMP_SPARES: 513,
  BURNER_BLOCK_SPARES: 515,
  BURNER_FRONT_FACE_SPARES: 516,
  BURNER_WATER_JACKET_SPARES: 517,
  BURNER_NOZZLE_SPARES: 518,

  // Shell Part IDs (replaceable — installed on slots, like Molds)
  UPPER_SHELL: 600,
  LOWER_SHELL: 601,

  // Burner Panel IDs (fixtures inside Upper Shell)
  BURNER_PANEL_1: 604,
  BURNER_PANEL_2: 605,
  BURNER_PANEL_3: 606,
} as const satisfies Record<string, number>;

/** Type for part ID values */
export type PartId = (typeof PART_IDS)[keyof typeof PART_IDS];

// ============================================
// Container Names for Display
// ============================================

export const CONTAINER_NAMES = {
  ONLINE: "Online",
  SPARES: "Spares",
  UPPER_SHELL: "Upper Shell",
  LOWER_SHELL: "Lower Shell",
  SHELL_SPARES: "Shell Spares",
  SHELL_PARTS_SPARES: "Shell Parts Spares",
  PANEL_SPARES: "Panel Spares",
  ARCHWAY_SPARES: "Archway Spares",
  SLAG_DOOR_SPARES: "Slag Door Spares",
  SUMP_SPARES: "Sump Spares",
  BURNER_BLOCK_SPARES: "Burner Block Spares",
  BURNER_FRONT_FACE_SPARES: "Burner Front Face Spares",
  BURNER_WATER_JACKET_SPARES: "Burner Water Jacket Spares",
  BURNER_NOZZLE_SPARES: "Burner Nozzle Spares",
} as const;

/**
 * Mapping from spares container PartId to display name
 * Uses Partial<Record<PartId, string>> since only some PartIds are spares containers
 */
export const SPARES_CONTAINER_NAMES: Partial<Record<PartId, string>> = {
  [PART_IDS.PANEL_SPARES]: CONTAINER_NAMES.PANEL_SPARES,
  [PART_IDS.ARCHWAY_SPARES]: CONTAINER_NAMES.ARCHWAY_SPARES,
  [PART_IDS.SLAG_DOOR_SPARES]: CONTAINER_NAMES.SLAG_DOOR_SPARES,
  [PART_IDS.SUMP_SPARES]: CONTAINER_NAMES.SUMP_SPARES,
  [PART_IDS.BURNER_BLOCK_SPARES]: CONTAINER_NAMES.BURNER_BLOCK_SPARES,
  [PART_IDS.BURNER_FRONT_FACE_SPARES]: CONTAINER_NAMES.BURNER_FRONT_FACE_SPARES,
  [PART_IDS.BURNER_WATER_JACKET_SPARES]:
    CONTAINER_NAMES.BURNER_WATER_JACKET_SPARES,
  [PART_IDS.BURNER_NOZZLE_SPARES]: CONTAINER_NAMES.BURNER_NOZZLE_SPARES,
};

/**
 * Ordered list of spares container IDs for consistent display
 */
export const SPARES_CONTAINER_ORDER = [
  PART_IDS.PANEL_SPARES,
  PART_IDS.ARCHWAY_SPARES,
  PART_IDS.SLAG_DOOR_SPARES,
  PART_IDS.SUMP_SPARES,
  PART_IDS.BURNER_BLOCK_SPARES,
  PART_IDS.BURNER_FRONT_FACE_SPARES,
  PART_IDS.BURNER_WATER_JACKET_SPARES,
  PART_IDS.BURNER_NOZZLE_SPARES,
] as const;

/**
 * Check if a container ID is a spare parts container
 */
export const isSpareContainer = (containerId: number | undefined): boolean => {
  if (containerId === undefined) return false;
  return (SPARES_CONTAINER_ORDER as readonly number[]).includes(containerId);
};

/**
 * Positions that have burners (have sub-components)
 */
export const BURNER_POSITIONS = [3, 5, 9] as const;

/**
 * Check if a position has a burner
 */
export const hasBurner = (positionId: number): boolean => {
  return (BURNER_POSITIONS as readonly number[]).includes(positionId);
};

// ============================================
// Shell Hierarchy Configuration
// ============================================

/**
 * Special position IDs for non-panel components.
 * Panels use positions 1-12, special components use 13-19.
 * Source of truth for ALL position IDs - do NOT duplicate elsewhere.
 */
export const SPECIAL_POSITION_IDS = {
  // Sumps (positions 13-15)
  LEFT_SUMP: 13,
  RIGHT_SUMP: 14,
  CENTER_SUMP: 15,
  // Special components (positions 16-17)
  ARCHWAY: 16,
  SLAG_DOOR: 17,
  // Shell positions (positions 18-19)
  LOWER_SHELL: 18,
  UPPER_SHELL: 19,
} as const;

/** Positions that belong under Upper Shell (19) - Sidewall panels */
export const UPPER_SHELL_CHILD_POSITIONS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
] as const;

/** Positions that belong under Lower Shell (18) - Sumps, Archway, Slag Door */
export const LOWER_SHELL_CHILD_POSITIONS = [13, 14, 15, 16, 17] as const;

/**
 * Check if a position belongs to Upper Shell
 */
export const isUpperShellChild = (positionId: number): boolean => {
  return (UPPER_SHELL_CHILD_POSITIONS as readonly number[]).includes(
    positionId,
  );
};

/**
 * Check if a position belongs to Lower Shell
 */
export const isLowerShellChild = (positionId: number): boolean => {
  return (LOWER_SHELL_CHILD_POSITIONS as readonly number[]).includes(
    positionId,
  );
};

/**
 * Check if a position is a shell container (18 or 19)
 */
export const isShellPosition = (positionId: number): boolean => {
  return (
    positionId === SPECIAL_POSITION_IDS.UPPER_SHELL ||
    positionId === SPECIAL_POSITION_IDS.LOWER_SHELL
  );
};

// ============================================
// Mapping from spares container to part type
// ============================================

export const SPARES_CONTAINER_TO_PART_TYPE: Partial<
  Record<PartId, PartTypeId>
> = {
  [PART_IDS.PANEL_SPARES]: PART_TYPE_IDS.SIDEWALL_PANEL,
  [PART_IDS.ARCHWAY_SPARES]: PART_TYPE_IDS.ARCHWAY,
  [PART_IDS.SLAG_DOOR_SPARES]: PART_TYPE_IDS.SLAG_DOOR,
  [PART_IDS.SUMP_SPARES]: PART_TYPE_IDS.SUMP,
  [PART_IDS.BURNER_BLOCK_SPARES]: PART_TYPE_IDS.BURNER_BLOCK,
  [PART_IDS.BURNER_FRONT_FACE_SPARES]: PART_TYPE_IDS.BURNER_FRONT_FACE,
  [PART_IDS.BURNER_WATER_JACKET_SPARES]: PART_TYPE_IDS.BURNER_WATER_JACKET,
  [PART_IDS.BURNER_NOZZLE_SPARES]: PART_TYPE_IDS.BURNER_NOZZLE,
};
